import React, { useEffect, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { ApolloError } from '@apollo/client';
import { useIntl } from 'react-intl';
import { capitalize } from 'lodash-es';
import ProductDetail, { ProductImageWrapper, ProductLinkWrapper }
  from '@fuww/library/src/Layouts/ProductPage';
import ImageCarousel from '@fuww/library/src/ImageCarousel';
import Button from '@fuww/library/src/Button';
import BackIcon from '@fuww/library/src/Icons/BackIcon';
import Anchor from '@fuww/library/src/Anchor';
import breakpoints from '@fuww/library/src/Theme/breakpoints';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'node:querystring';
import MoreProductsAvailable from './MoreProductsAvailable';
import Head from '../Head';
import HomeLink from '../HomeLink';
import getPriceRange from '../../lib/getPriceRange';
import withQuery from '../../lib/withQuery';
import LinkAndAnchor from '../LinkAndAnchor';
import ImageWithUrls from '../ImageWithUrls';
import throw404 from '../../lib/throw404';
import throw403 from '../../lib/throw403';
import messages from '../../lib/messages.json';
import getProxiedImages from '../../lib/getProxiedImages';
import { useSiteContext } from '../SiteContext';
import { getRouterState } from '../../lib/savedRouterState';
import {
  ProductQuery,
  ProductQueryVariables,
} from '../../lib/graphql/api/graphql';

interface ImageProperties {
  id: number | string;
  urls: string[];
}

interface LastProductsProperties {
  route?: string;
  query?: ParsedUrlQuery;
}

const displayGender = (genders: string[]) => (
  genders.includes('MEN') && genders.includes('WOMEN')
    ? 'Unisex'
    : genders.map((gender) => capitalize(gender)).join(', ')
);

const productPropertyTypes = {
  marketId: PropTypes.number,
  standId: PropTypes.number,
  hasBreadCrumbs: PropTypes.bool,
  slug: PropTypes.string,
  backToProductsRoute: PropTypes.string,
  buyText: PropTypes.bool,
};

type ProductProperties = InferProps<typeof productPropertyTypes> & {
  data: ProductQuery;
  variables: ProductQueryVariables;
  error?: ApolloError;
};

const Product = ({
  data: {
    product,
  } = {},
  variables,
  marketId,
  standId,
  slug,
  error,
  hasBreadCrumbs,
  backToProductsRoute,
  buyText,
}: ProductProperties) => {
  const buyTextValue = buyText ?? false;
  const intl = useIntl();
  const { locale } = useSiteContext();
  const router = useRouter();
  const [
    lastProductsQuery, setLastProductsQuery,
  ] = useState<LastProductsProperties | null>(null);

  useEffect(() => {
    setLastProductsQuery(getRouterState('products'));
  }, []);

  if (
    error?.graphQLErrors.some(({ message }) => message === 'Access denied')
  ) {
    return (
      <>
        {typeof marketId === 'number' && (
          <MoreProductsAvailable
            variables={{
              marketId,
              standId,
              locales: [locale],
            }}
          />
        )}
        {throw403()}
      </>
    );
  }

  if (!product) return throw404();

  const {
    description,
    brand,
    genders,
    ageGroups,
    colorNames,
    sizeNames,
    orderQuantities,
    deliveryTimes,
    material,
    season,
    name: title,
    retailerName,
    shortDescription,
    summary,
    id,
    url,
    images,
    print: printName,
    pricesWithCurrencies,
    gtins,
  } = product;

  const {
    id: brandId,
    name: brandName,
    profiles,
  } = { profiles: [], ...brand };

  const {
    minimum: quantityFrom,
  } = { ...orderQuantities };

  const price = getPriceRange(pricesWithCurrencies, locale);
  const brandLink = profiles[0]
    ? (
      <ProductLinkWrapper>
        <LinkAndAnchor
          route="company-profile"
          params={{ slug: profiles[0].page.slug }}
        >
          {brandName}
        </LinkAndAnchor>
      </ProductLinkWrapper>
    )
    : (
      brandName
    );

  const details = [
    {
      name: intl.formatMessage(messages['product.gender']),
      value: displayGender(genders),
    },
    {
      name: intl.formatMessage(messages['product.ageGroup']),
      value: ageGroups.map(
        (ageGroup: string) => capitalize(ageGroup),
      ).join(', '),
    },
    ...(quantityFrom && quantityFrom > 1 ? [{
      name: intl.formatMessage(messages['product.orderQuantity']),
      value: quantityFrom,
    }] : []),
    {
      name: intl.formatMessage(messages['product.deliveryTime']),
      value: deliveryTimes.join(', '),
    },
    {
      name: intl.formatMessage(messages['product.colors']),
      value: colorNames.map(
        (colorName: string) => capitalize(colorName),
      ).join(', '),
    },
    {
      name: intl.formatMessage(messages['product.sizes']),
      value: sizeNames.join(', '),
    },
    {
      name: intl.formatMessage(messages['product.material']),
      value: material,
    },
    {
      name: intl.formatMessage(messages['product.season']),
      value: season,
    },
    {
      name: intl.formatMessage(messages['product.brandName']),
      value: brandLink,
    },
    {
      name: intl.formatMessage(messages['product.pattern']),
      value: printName,
    },
    {
      name: intl.formatMessage(messages['product.price']),
      value: price,
    },
    {
      name: intl.formatMessage(messages['product.gtin']),
      value: gtins.join(', '),
    },
  ];

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor
      route="products"
      params={{ marketId: marketId?.toString(), standId: standId?.toString() }}
    >
      Products
    </LinkAndAnchor>,
    <LinkAndAnchor
      route="product"
      params={{
        marketId: marketId?.toString(),
        standId: standId?.toString(),
        id: id.toString(),
      }}
    >
      {title}
    </LinkAndAnchor>,
  ];

  const pageTitle = `${title} | ${brandName}`;

  return (
    <>
      <Head
        title={pageTitle}
        description={
          summary
          ?? intl.formatMessage(messages['markets.description'])
        }
        subjectType="Product"
        subjectId={id}
        payload={{ marketId, standId, brandId }}
      />
      <ProductDetail
        images={(
          images.length > 1 ? (
            <ProductImageWrapper>
              <ImageCarousel>
                {images.map((image: ImageProperties, index: number) => {
                  const { imageUrls, srcSet } = getProxiedImages(
                    variables,
                    image.urls,
                  );
                  const isFirstImage = index === 0;
                  const fetchpriority = isFirstImage ? 'high' : 'low';
                  return (
                    <ImageWithUrls
                      key={image.id}
                      imageUrls={imageUrls}
                      alt={pageTitle}
                      sizes={
                      `(min-width: 1440px) 850px, (min-width: ${
                        breakpoints.lg
                      }px) 59vw, (min-width: ${breakpoints.md
                      }px) 58vw, (min-width: ${breakpoints.sm
                      }px) 98vw, 96vw`
                    }
                      srcSet={srcSet}
                      width="100%"
                      preload={isFirstImage}
                      finalFetchpriority={fetchpriority}
                      placeholderFetchpriority={fetchpriority}
                    />
                  );
                })}
              </ImageCarousel>
            </ProductImageWrapper>
          ) : (
            images.map((image: ImageProperties) => {
              const { imageUrls, srcSet } = getProxiedImages(
                variables,
                image.urls,
              );
              return (
                <ProductImageWrapper key={image.id}>
                  <ImageWithUrls
                    imageUrls={imageUrls}
                    alt={pageTitle}
                    sizes={
                      `(min-width: 1440px) 850px, (min-width: ${
                        breakpoints.lg
                      }px) 59vw, (min-width: ${breakpoints.md
                      }px) 58vw, (min-width: ${breakpoints.sm
                      }px) 98vw, 96vw`
                    }
                    srcSet={srcSet}
                    width="100%"
                    finalFetchpriority="high"
                    placeholderFetchpriority="high"
                    preload
                  />
                </ProductImageWrapper>
              );
            })
          )
        )}
        title={title}
        breadcrumbs={breadcrumbs}
        hasBreadCrumbs={hasBreadCrumbs ?? true}
        body={description ?? shortDescription}
        button={(
          url && (
            <Anchor styled href={url} target="_blank">
              <Button className="contact-brand" variant="contained">
                {buyTextValue
                  ? intl.formatMessage(
                    messages['product.buyAt'],
                    { retailerName },
                  )
                  : intl.formatMessage(messages['product.contact'])}
              </Button>
            </Anchor>
          )
        )}
        backToOverview={(
          <LinkAndAnchor
            route={lastProductsQuery?.route ?? backToProductsRoute}
            params={lastProductsQuery?.query ?? {
              ...(standId ? { standId: standId.toString() } : {}),
              ...(marketId ? { marketId: marketId.toString() } : {}),
              ...(slug ? { slug } : {}),
            }}
            onClick={(event: React.SyntheticEvent) => {
              if (lastProductsQuery?.query) {
                event.preventDefault();
                router.back();
              }
            }}
            useRouter
            styled
          >
            <BackIcon />
            {intl.formatMessage(messages['product.backToProducts'])}
          </LinkAndAnchor>
        )}
        details={details}
      />
    </>
  );
};

Product.propTypes = productPropertyTypes;

export default withQuery<ProductQuery, ProductQueryVariables, false, {
  marketId?: number;
  standId?: number;
  hasBreadCrumbs?: boolean;
  slug?: string;
  backToProductsRoute?: string;
  buyText?: boolean;
}>(Product);
